import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const colors = {
  coral: '#E8847C',
  mint: '#7ECEC1',
};

const NAV_ITEMS = [
  { to: '/#purpose', id: 'purpose', label: '開催趣旨' },
  { to: '/#program', id: 'program', label: 'プログラム' },
  { to: '/#speakers', id: 'speakers', label: '登壇者' },
  { to: '/#venue', id: 'venue', label: '会場' },
  { to: '/#sponsorship', id: 'sponsorship', label: '協賛プラン' },
  { to: '/#faq', id: 'faq', label: 'FAQ' },
];

type HeaderVariant = 'home' | 'event';

interface HeaderProps {
  variant?: HeaderVariant;
}

export default function Header({ variant = 'home' }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const isHome = variant === 'home';
  const headerBg = isHome
    ? isScrolled
      ? 'bg-white/90 backdrop-blur-xl shadow-lg'
      : 'bg-transparent'
    : isScrolled
      ? 'bg-white/95 backdrop-blur-xl shadow-lg'
      : 'bg-white/80 backdrop-blur-md';

  const textColor = isHome && !isScrolled ? 'text-gray-800' : 'text-gray-900';

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${headerBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center flex-shrink-0">
              <img src="/images/logo-icon.png" alt="国際女性デー ロゴ" className="w-full h-full object-contain" />
            </div>
            <div
              className={`flex flex-col leading-tight transition-colors ${textColor}`}
              style={{ fontFamily: "'Shippori Mincho', serif" }}
            >
              <span className="text-base sm:text-lg font-bold">国際女性デー</span>
              <span className={`text-xs sm:text-sm font-medium ${isHome && !isScrolled ? 'text-gray-600' : 'text-gray-500'}`}>チャリティイベント 2026</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((item) =>
              isHome ? (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-sm font-medium cursor-pointer transition-all hover:opacity-70 relative group"
                >
                  {item.label}
                  <span
                    className="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300"
                    style={{ backgroundColor: colors.coral }}
                  />
                </button>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-all relative group"
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 bg-[#7ECEC1]" />
                </Link>
              )
            )}
            {!isHome && (
              <span className="text-sm font-bold" style={{ color: colors.mint }}>
                イベントの価値
              </span>
            )}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href="https://peatix.com/event/4805808/"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white whitespace-nowrap rounded-xl bg-[#7ECEC1] hover:bg-[#6ebdb0] transition-colors"
            >
              <span>チケット購入</span>
              <i className="ri-arrow-right-up-line" />
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            >
              <i className={`text-2xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} ${textColor}`} />
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-100 pt-4">
            <nav className="flex flex-col gap-3">
              {NAV_ITEMS.map((item) =>
                isHome ? (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="text-left px-4 py-2 text-gray-700 font-medium cursor-pointer hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-left px-4 py-2 text-gray-700 font-medium hover:bg-gray-50 rounded-lg"
                  >
                    {item.label}
                  </Link>
                )
              )}
              {!isHome && (
                <span className="px-4 py-2 font-bold" style={{ color: colors.mint }}>
                  イベントの価値
                </span>
              )}
              <a
                href="https://peatix.com/event/4805808/"
                target="_blank"
                rel="noopener noreferrer"
                className="mx-4 mt-2 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-bold text-white rounded-xl bg-[#7ECEC1] hover:bg-[#6ebdb0]"
              >
                <span>チケット購入</span>
                <i className="ri-arrow-right-up-line" />
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
